import React, { useState, useEffect, useMemo } from 'react';
import { parse } from 'yaml';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { usePipelineMutation } from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectProjectPath, selectBranch, setCurrentStep } from 'app/sharedSlice';
import { selectSnowflakeAccountLabel } from 'app/snowflakeSlice';
import { selectDataProductManager, selectName } from '../reducers/astraZenecaSlice';
import {
  selectDataProductVersion,
  selectNumberOfSelectedSourceTables,
  selectNumberOfGeneratedTests,
  selectSoleOutput,
  selectGeneratedTests,
  setNumberOfSelectedSourceTables,
  setNumberOfGeneratedTests,
  setGeneratedTests,
} from '../../data-product-builder/reducers/builderSlice';
import StepWrapper from 'components/StepWrapper';
import OverviewBlock from 'components/OverviewBlock';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import TestBadge from '../components/TestBadge';
import type { ISoleOutput, ITest } from 'utils/types';

export interface DataProductSummaryStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function DataProductSummaryStep(props: DataProductSummaryStepProps): JSX.Element {
  const dispatch = useAppDispatch();
  const dataProductManager = useAppSelector(selectDataProductManager);
  const dataProductDisplayName = useAppSelector(selectName);
  const dataProductVersion = useAppSelector(selectDataProductVersion);
  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);
  const snowflakeAccountLabel = useAppSelector(selectSnowflakeAccountLabel);
  const numberOfSelectedSourceTables = useAppSelector(selectNumberOfSelectedSourceTables);
  const numberOfGeneratedTests = useAppSelector(selectNumberOfGeneratedTests);

  // Generated tests
  const soleOutput = useAppSelector(selectSoleOutput);
  const parsedSoleOutput: ISoleOutput = useMemo(() => parse(soleOutput), [soleOutput]);
  const tables = useMemo(
    () => parsedSoleOutput?.sources?.map((source: any) => source.tables).flat() ?? [],
    [parsedSoleOutput],
  );
  const generatedTests = useAppSelector(selectGeneratedTests);

  useEffect(() => {
    dispatch(setNumberOfSelectedSourceTables(tables.length));
    const columnsWithTests = tables.map((table) => table.columns.filter((column: any) => column.tests.length > 0));
    const numberOfGeneratedTests = columnsWithTests.flat().length;
    dispatch(setNumberOfGeneratedTests(numberOfGeneratedTests));

    const tmpGeneratedTests: ITest[] = [];
    if (parsedSoleOutput !== null && Array.isArray(parsedSoleOutput.sources)) {
      for (const source of parsedSoleOutput.sources) {
        for (const table of tables) {
          for (const column of table.columns) {
            for (const test of column.tests) {
              tmpGeneratedTests.push({
                schema: source.schema,
                table: table.name,
                column: column.name,
                testType: test,
              });
            }
          }
        }
      }
    }

    dispatch(setGeneratedTests(tmpGeneratedTests));
  }, [tables]);

  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage] = useState<string>(
    'Oops! Failed to trigger the pipeline. Please try again or contact support@dataops.live if the issue persists.',
  );

  const [pipeline] = usePipelineMutation();

  const stats = [
    { name: 'Source tables', value: numberOfSelectedSourceTables },
    { name: 'Source tests', value: numberOfGeneratedTests },
  ];

  const onContinue = (): void => {
    setLoading(true);
    setFailed(false);
    pipeline({ dataProductName: 'slim', projectName: projectPath, branch, mergeMain: false, camelCase: false })
      .unwrap()
      .then((res: any) => {
        props.onContinue();
      })
      .catch((err) => {
        console.log(err);
        setFailed(true);
        // Currently we use a generic error message and not the one returned by the backend
        // setErrorMessage(err.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* eslint-disable */
  return (
    <StepWrapper
      title="Review your Data Product"
      subtitle="Review the Data Product information provided before proceeding with model development"
      onBack={() => props.onBack()}
      onContinue={() => onContinue()}
      continueDisabled={loading}
      isLoading={loading}
      isLastStep={true}
    >
      <div className="px-6 w-full max-w-[70rem] mx-[auto]">
        <div className="flex">
          <OverviewBlock
            dividerText="Definition"
            showEditButton
            onEditClick={() => dispatch(setCurrentStep(0))}
            showRightBorder
          >
            <dl className="grid grid-cols-2">
              <div className="border-gray-100 px-4 py-3">
                <dt className="text-sm font-semibold text-gray-900">Data Product Name</dt>
                <dd className="text-sm text-gray-700 truncate">{dataProductDisplayName}</dd>
              </div>
              <div className="border-gray-100 px-4 py-3">
                <dt className="text-sm font-semibold text-gray-900">Data Product Manager</dt>
                <dd className="text-sm text-gray-700 truncate">{dataProductManager}</dd>
              </div>
              <div className="border-gray-100 px-4 py-3">
                <dt className="text-sm font-semibold text-gray-900">Data Product Version</dt>
                <dd className="text-sm text-gray-700">{dataProductVersion}</dd>
              </div>
              <div className="border-gray-100 px-4 py-3">
                <dt className="text-sm font-semibold text-gray-900">Project</dt>
                <dd className="text-sm text-gray-700 truncate">{projectPath}</dd>
              </div>
            </dl>
          </OverviewBlock>
        </div>

        <OverviewBlock
          dividerText="Dataset source"
          backgroundColor="bg-gray-50"
          showEditButton
          onEditClick={() => dispatch(setCurrentStep(1))}
        >
          <div className="px-6 text-sm text-gray-600">
            The metadata was imported from the following Snowflake account:{' '}
            <span className="font-semibold text-gray-800">{snowflakeAccountLabel}</span>
          </div>
        </OverviewBlock>

        <OverviewBlock dividerText="Source tests">
          <div className="max-w-[20rem] mx-[auto] text-sm text-gray-600 px-4">
            <dl className="border border-gray-300 rounded-md overflow-hidden mx-auto grid grid-cols-2 gap-px bg-gray-900/5">
              {stats.map((stat) => (
                <div
                  key={stat.name}
                  className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 bg-white px-3 py-2"
                >
                  <dt className="text-sm font-medium leading-6 text-gray-500">{stat.name}</dt>
                  <dd className="w-full flex-none text-3xl font-medium leading-10 tracking-tight text-gray-900">
                    {stat.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          {generatedTests.length > 0 && (
            <>
              <div className="px-6 text-sm text-gray-600 text-center mt-4 mb-2">
                The following tests were generated based on the constraints on the Snowflake tables.
              </div>
              <div className="overflow-x-scroll">
                <table className="w-full xl:w-3/4 mt-2 mx-[auto] px-2 divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="truncate py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                      >
                        Schema
                      </th>
                      <th scope="col" className="truncate px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Table
                      </th>
                      <th scope="col" className="truncate px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Column
                      </th>
                      <th scope="col" className="truncate px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Test type
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {generatedTests.map((test) => (
                      <tr key={`${test.schema}-${test.table}-${test.column}-${test.testType}`}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                          {test.schema}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{test.table}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{test.column}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <TestBadge testType={test.testType} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {generatedTests.length === 0 && (
            <div className="max-w-[40rem] mx-[auto] mt-6 rounded-md bg-blue-50 p-4 flex">
              <div className="flex-shrink-0">
                <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
              </div>
              <div>
                <div className="ml-3 flex-1 md:flex md:justify-between">
                  <p className="text-sm text-blue-700 font-semibold">All Set!</p>
                </div>
                <div className="ml-3 flex-1 md:flex md:justify-between">
                  <p className="text-sm text-blue-700">
                    No constraints were found on your source data, so no tests were auto-generated. Feel free to
                    continue, and we&apos;ll help generate tests if constraints are added later.
                  </p>
                </div>
              </div>
            </div>
          )}
        </OverviewBlock>
      </div>
      <LoadingAndErrorSection isLoading={loading} isFailed={failed} errorMessage={errorMessage} hideLoading={true} />
    </StepWrapper>
  );
}
